/**
 * plyseq.ts — Decoder for .plyseq / .4dms files.
 *
 * Mirrors the reader half of plyseq.py. Parses the v2 container header,
 * exposes random access to frames by their original frame ID, extracts
 * per-vertex attributes, and reconstructs standalone .ply files.
 *
 * File layout (v2):
 *   magic(8) | n_frames(u32) | frame_start(u32) | frame_end(u32) |
 *   fps(f32) | n_runs(u32) | props_len(u32) | props_blob(utf8) |
 *   { run_len(u32), n_verts(u32) }[n_runs] | data_offset(u64) |
 *   <vertex data...>
 *
 * Usage:
 *   const reader = await PlySeqReader.open(url)        // HTTP Range
 *   const reader = await PlySeqReader.open(file)       // File / Blob
 *   const reader = await PlySeqReader.open(arrayBuf)   // in-memory
 *
 *   const frame = await reader.readFrame(128)
 *   const xyz   = reader.getAttribute(frame, 'x')      // Float32Array
 *   await reader.downloadFramePly(128)
 */

const MAGIC = new Uint8Array([0x50, 0x4c, 0x59, 0x53, 0x45, 0x51, 0x02, 0x00]); // 'PLYSEQ\x02\x00'

const PLY_TYPE_SIZE: Record<string, number> = {
  float: 4, float32: 4,
  double: 8, float64: 8,
  uchar: 1, uint8: 1,
  char: 1, int8: 1,
  ushort: 2, uint16: 2,
  short: 2, int16: 2,
  uint: 4, uint32: 4,
  int: 4, int32: 4,
};

export interface PropDef {
  name: string;
  type: string;
  size: number;
  offset: number; // byte offset within a vertex record
}

export interface RleRun {
  runLen: number;
  nVerts: number;
}

export interface FrameData {
  frameId: number;
  index: number;
  nVertices: number;
  data: Uint8Array;       // raw interleaved vertex bytes
  stride: number;         // bytes per vertex
  props: PropDef[];
  littleEndian: boolean;
}

export interface PlySeqInfo {
  frames: { start: number; end: number; count: number };
  fps: number;
  duration: number | null;
  properties: { name: string; type: string }[];
  perVertexBytes: number;
  runs: RleRun[];
  dataOffset: number;
  totalDataBytes: number;
  fileSize: number;
}

export type TypedNumberArray =
  | Float32Array | Float64Array
  | Uint8Array | Int8Array
  | Uint16Array | Int16Array
  | Uint32Array | Int32Array;

// ─────────────────────────── Byte sources ───────────────────────────

export interface ByteSource {
  readonly size: number;
  read(offset: number, length: number): Promise<Uint8Array>;
}

export class ArrayBufferSource implements ByteSource {
  private buf: ArrayBuffer;
  constructor(buf: ArrayBuffer) {
    this.buf = buf;
  }
  get size() { return this.buf.byteLength; }
  async read(offset: number, length: number): Promise<Uint8Array> {
    return new Uint8Array(this.buf, offset, length);
  }
}

export class BlobSource implements ByteSource {
  private blob: Blob;
  constructor(blob: Blob) {
    this.blob = blob;
  }
  get size() { return this.blob.size; }
  async read(offset: number, length: number): Promise<Uint8Array> {
    const slice = this.blob.slice(offset, offset + length);
    return new Uint8Array(await slice.arrayBuffer());
  }
}

export class RangeFetchSource implements ByteSource {
  size: number;
  private url: string;
  private constructor(url: string, size: number) {
    this.url = url;
    this.size = size;
  }
  static async open(url: string): Promise<RangeFetchSource> {
    const head = await fetch(url, { method: 'HEAD' });
    if (!head.ok) throw new Error(`HEAD ${url} failed: ${head.status}`);
    const len = head.headers.get('content-length');
    if (!len) throw new Error(`no content-length on ${url}`);
    return new RangeFetchSource(url, parseInt(len, 10));
  }
  async read(offset: number, length: number): Promise<Uint8Array> {
    const end = offset + length - 1;
    const res = await fetch(this.url, { headers: { Range: `bytes=${offset}-${end}` } });
    if (!res.ok && res.status !== 206) throw new Error(`Range ${offset}-${end} on ${this.url} failed: ${res.status}`);
    return new Uint8Array(await res.arrayBuffer());
  }
}

// ─────────────────────────── Reader ───────────────────────────

export class PlySeqReader {
  nFrames!: number;
  frameStart!: number;
  frameEnd!: number;
  fps!: number;
  formatLine!: string;
  propLines!: string[];
  props!: PropDef[];
  perVertexBytes!: number;
  runs!: RleRun[];
  vertexCounts!: number[];
  dataOffset!: number;
  littleEndian!: boolean;

  source: ByteSource;
  private frameByteOffsets!: number[];

  private constructor(source: ByteSource) {
    this.source = source;
  }

  static async open(input: ByteSource | ArrayBuffer | Blob | string): Promise<PlySeqReader> {
    let src: ByteSource;
    if (input instanceof ArrayBuffer) src = new ArrayBufferSource(input);
    else if (typeof Blob !== 'undefined' && input instanceof Blob) src = new BlobSource(input);
    else if (typeof input === 'string') src = await RangeFetchSource.open(input);
    else src = input as ByteSource;

    const reader = new PlySeqReader(src);
    await reader.parseHeader();
    return reader;
  }

  private async parseHeader(): Promise<void> {
    // Fixed prefix: magic(8) + 6×u32 = 32 bytes
    const PREFIX = 32;
    const head = await this.source.read(0, PREFIX);

    for (let i = 0; i < MAGIC.length; i++) {
      if (head[i] !== MAGIC[i]) throw new Error('Not a plyseq v2 file (bad magic)');
    }

    const headView = new DataView(head.buffer, head.byteOffset, head.byteLength);
    this.nFrames    = headView.getUint32(8,  true);
    this.frameStart = headView.getUint32(12, true);
    this.frameEnd   = headView.getUint32(16, true);
    this.fps        = headView.getFloat32(20, true);
    const nRuns     = headView.getUint32(24, true);
    const propsLen  = headView.getUint32(28, true);

    // props blob + RLE table + data_offset(u64)
    const tail = await this.source.read(PREFIX, propsLen + nRuns * 8 + 8);
    const tailView = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);

    const propsText = new TextDecoder('utf-8').decode(tail.subarray(0, propsLen));
    const lines = propsText.trim().split('\n');
    this.formatLine = lines[0];
    this.propLines = lines.slice(1);
    this.littleEndian = this.formatLine.includes('little');

    const { props, stride } = parsePropLines(this.propLines);
    this.props = props;
    this.perVertexBytes = stride;

    this.runs = [];
    let cursor = propsLen;
    for (let i = 0; i < nRuns; i++) {
      const runLen = tailView.getUint32(cursor,     true);
      const nVerts = tailView.getUint32(cursor + 4, true);
      this.runs.push({ runLen, nVerts });
      cursor += 8;
    }

    // u64 data_offset (assemble from two u32s — JS Number is safe up to 2^53)
    const lo = tailView.getUint32(cursor,     true);
    const hi = tailView.getUint32(cursor + 4, true);
    this.dataOffset = hi * 0x1_0000_0000 + lo;

    // Expand RLE counts and precompute per-frame byte offsets
    this.vertexCounts = [];
    for (const { runLen, nVerts } of this.runs) {
      for (let i = 0; i < runLen; i++) this.vertexCounts.push(nVerts);
    }
    this.frameByteOffsets = new Array(this.vertexCounts.length + 1);
    this.frameByteOffsets[0] = 0;
    for (let i = 0; i < this.vertexCounts.length; i++) {
      this.frameByteOffsets[i + 1] = this.frameByteOffsets[i] + this.vertexCounts[i] * this.perVertexBytes;
    }
  }

  // ─── Frame access ───

  frameIdToIndex(frameId: number): number {
    if (frameId < this.frameStart || frameId > this.frameEnd) {
      throw new RangeError(`Frame ID ${frameId} out of range [${this.frameStart}, ${this.frameEnd}]`);
    }
    return frameId - this.frameStart;
  }

  has(frameId: number): boolean {
    return frameId >= this.frameStart && frameId <= this.frameEnd;
  }

  getVertexCount(frameId: number): number {
    return this.vertexCounts[this.frameIdToIndex(frameId)];
  }

  async readFrameRaw(frameId: number): Promise<Uint8Array> {
    const idx = this.frameIdToIndex(frameId);
    const offset = this.dataOffset + this.frameByteOffsets[idx];
    const nBytes = this.vertexCounts[idx] * this.perVertexBytes;
    return await this.source.read(offset, nBytes);
  }

  async readFrame(frameId: number): Promise<FrameData> {
    const idx = this.frameIdToIndex(frameId);
    const data = await this.readFrameRaw(frameId);
    return {
      frameId,
      index: idx,
      nVertices: this.vertexCounts[idx],
      data,
      stride: this.perVertexBytes,
      props: this.props,
      littleEndian: this.littleEndian,
    };
  }

  /** Extract one property as a TypedArray (copies). */
  getAttribute(frame: FrameData, name: string): TypedNumberArray {
    const prop = frame.props.find(p => p.name === name);
    if (!prop) throw new Error(`Property "${name}" not found`);
    const n = frame.nVertices;
    const view = new DataView(frame.data.buffer, frame.data.byteOffset, frame.data.byteLength);
    const le = frame.littleEndian;

    let out: TypedNumberArray;
    let getter: (o: number) => number;
    switch (prop.type) {
      case 'float':  case 'float32': out = new Float32Array(n); getter = o => view.getFloat32(o, le); break;
      case 'double': case 'float64': out = new Float64Array(n); getter = o => view.getFloat64(o, le); break;
      case 'uchar':  case 'uint8':   out = new Uint8Array(n);   getter = o => view.getUint8(o);       break;
      case 'char':   case 'int8':    out = new Int8Array(n);    getter = o => view.getInt8(o);        break;
      case 'ushort': case 'uint16':  out = new Uint16Array(n);  getter = o => view.getUint16(o, le);  break;
      case 'short':  case 'int16':   out = new Int16Array(n);   getter = o => view.getInt16(o, le);   break;
      case 'uint':   case 'uint32':  out = new Uint32Array(n);  getter = o => view.getUint32(o, le);  break;
      case 'int':    case 'int32':   out = new Int32Array(n);   getter = o => view.getInt32(o, le);   break;
      default: throw new Error(`Unsupported PLY type: ${prop.type}`);
    }
    for (let i = 0; i < n; i++) out[i] = getter(i * frame.stride + prop.offset);
    return out;
  }

  /** Convenience: x/y/z packed as Float32Array of length nVertices*3. */
  getXYZ(frame: FrameData): Float32Array {
    const x = this.getAttribute(frame, 'x') as Float32Array;
    const y = this.getAttribute(frame, 'y') as Float32Array;
    const z = this.getAttribute(frame, 'z') as Float32Array;
    const n = frame.nVertices;
    const out = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) {
      out[i * 3]     = x[i];
      out[i * 3 + 1] = y[i];
      out[i * 3 + 2] = z[i];
    }
    return out;
  }

  // ─── Export ───

  /** Build a standalone .ply file as a Uint8Array. */
  async exportFramePly(frameId: number): Promise<Uint8Array> {
    const idx = this.frameIdToIndex(frameId);
    const raw = await this.readFrameRaw(frameId);
    const nVerts = this.vertexCounts[idx];

    let header = 'ply\n';
    header += this.formatLine + '\n';
    header += `element vertex ${nVerts}\n`;
    for (const pl of this.propLines) header += pl + '\n';
    header += 'end_header\n';

    const headerBytes = new TextEncoder().encode(header);
    const out = new Uint8Array(headerBytes.length + raw.length);
    out.set(headerBytes, 0);
    out.set(raw, headerBytes.length);
    return out;
  }

  /** Trigger a browser download for one frame. */
  async downloadFramePly(frameId: number, filename?: string): Promise<void> {
    const bytes = await this.exportFramePly(frameId);
    const blob = new Blob([bytes], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename ?? `frame_${frameId}.ply`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // ─── Misc ───

  totalDataBytes(): number {
    return this.frameByteOffsets[this.frameByteOffsets.length - 1];
  }

  *frameIds(): IterableIterator<number> {
    for (let fid = this.frameStart; fid <= this.frameEnd; fid++) yield fid;
  }

  info(): PlySeqInfo {
    return {
      frames: { start: this.frameStart, end: this.frameEnd, count: this.nFrames },
      fps: this.fps,
      duration: this.fps > 0 ? this.nFrames / this.fps : null,
      properties: this.props.map(p => ({ name: p.name, type: p.type })),
      perVertexBytes: this.perVertexBytes,
      runs: this.runs.slice(),
      dataOffset: this.dataOffset,
      totalDataBytes: this.totalDataBytes(),
      fileSize: this.source.size,
    };
  }

  /** Pretty-print a summary, mirroring the Python CLI `info` output. */
  printInfo(log: (s: string) => void = console.log): void {
    const i = this.info();
    log(`Frames:      ${i.frames.start} ~ ${i.frames.end} (${i.frames.count} frames)`);
    if (i.fps > 0) {
      log(`FPS:         ${i.fps}`);
      log(`Duration:    ${(i.duration as number).toFixed(2)}s`);
    }
    log(`Properties:  ${i.properties.length} fields, ${i.perVertexBytes} bytes/vertex`);
    log(`Runs:        ${i.runs.length}`);
    let acc = 0;
    for (const r of i.runs) {
      const idStart = i.frames.start + acc;
      const idEnd = idStart + r.runLen - 1;
      log(`  ID ${idStart}~${idEnd}: ${r.runLen} frames × ${r.nVerts} vertices ` +
          `(${(r.nVerts * i.perVertexBytes).toLocaleString()} bytes/frame)`);
      acc += r.runLen;
    }
    log(`Data:        ${i.totalDataBytes.toLocaleString()} bytes (${(i.totalDataBytes / 1e6).toFixed(1)} MB)`);
    log(`File size:   ${i.fileSize.toLocaleString()} bytes (${(i.fileSize / 1e6).toFixed(1)} MB)`);
    log(`Overhead:    ${(i.fileSize - i.totalDataBytes).toLocaleString()} bytes`);
  }
}

// ─────────────────────────── Helpers ───────────────────────────

function parsePropLines(propLines: string[]): { props: PropDef[]; stride: number } {
  const props: PropDef[] = [];
  let offset = 0;
  for (const line of propLines) {
    const parts = line.trim().split(/\s+/);
    if (parts.length !== 3 || parts[0] !== 'property') {
      throw new Error(`Unsupported property line (list?): ${line}`);
    }
    const [, type, name] = parts;
    const size = PLY_TYPE_SIZE[type];
    if (size === undefined) throw new Error(`Unsupported PLY type: ${type}`);
    props.push({ name, type, size, offset });
    offset += size;
  }
  return { props, stride: offset };
}
